// Sanitizes HTML that originated outside our own app — inbound email
// bodies synced from IMAP/Graph/Gmail (EmailClient.js, OutlookWorkspace.js).
//
// Anyone can email a connected mailbox, and that raw HTML previously went
// straight into dangerouslySetInnerHTML with no XSS protection at all
// (blockRemoteImages in OutlookWorkspace.js only strips remote <img> src
// attributes for privacy — it never touched <script>, inline event
// handlers, or javascript: URLs). This is the one shared place that closes
// that gap, so every call site sanitizes the same way instead of each
// screen inventing its own allowlist.
import DOMPurify from "dompurify";

// Deliberately generous on layout/formatting tags (real emails are full of
// them) but never allows anything that can execute script: no <script>,
// no on*= handlers, no javascript:/data: URLs, no <iframe>/<object>/<embed>,
// no <form>/<input> (a spoofed login form embedded in an email body).
const EMAIL_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "a", "b", "strong", "i", "em", "u", "s", "strike", "p", "div", "span",
    "br", "hr", "ul", "ol", "li", "blockquote", "pre", "code",
    "table", "thead", "tbody", "tfoot", "tr", "td", "th",
    "h1", "h2", "h3", "h4", "h5", "h6", "img", "font", "sub", "sup", "small",
  ],
  ALLOWED_ATTR: [
    "href", "target", "rel", "style", "class", "src", "alt", "title",
    "width", "height", "align", "valign", "colspan", "rowspan",
    "border", "cellpadding", "cellspacing", "data-blocked-src", "color", "face", "size",
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input", "textarea", "button", "select", "link", "meta", "base"],
  FORBID_ATTR: ["srcset"],
};

export function sanitizeEmailHtml(html) {
  if (!html) return "";
  try {
    return DOMPurify.sanitize(String(html), EMAIL_SANITIZE_CONFIG);
  } catch (e) {
    // Fail closed for untrusted content — an unrenderable/empty body is
    // far safer than falling back to the unsanitized original.
    return "";
  }
}
